/**
 * Cumplimiento SISS — tablero regulatorio.
 *
 * Perfil Regulatorio: vigila los indicadores que el Instructivo SISS de
 * telelectura (jun-2024) exige: tasa de lectura efectiva, avisos de fuga
 * respaldados, auditoría de lecturas digitales e inventario de medidores
 * migrados.  Reúne lo que la plataforma ya mide (cobertura de lecturas vía
 * calidad de datos) y deja trazables los reportes exportables al regulador.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";

interface DqMetrics {
  cobertura_lecturas_pct: number;
  gaps: number;
  lecturas_imposibles: number;
  reglas: Array<{ codigo: string; descripcion: string; impacto: string; eventos_24h: number }>;
  _source?: string;
}

const REQUISITOS_SISS = [
  {
    elemento: "Telelectura y sistemas de captura",
    estado: "cumple",
    detalle: "Series temporales densas por medidor disponibles.",
  },
  {
    elemento: "Aviso de fuga al cliente",
    estado: "cumple",
    detalle: "Detección y registro de alarmas con evidencia auditada.",
    to: "/fugas",
  },
  {
    elemento: "Auditoría de lecturas y alertas",
    estado: "parcial",
    detalle: "Validación automática activa; falta cruce sistemático con terreno.",
    to: "/calidad",
  },
  {
    elemento: "Seguridad de datos y acceso",
    estado: "cumple",
    detalle: "RBAC por rol + cadena de auditoría hash.",
    to: "/supervisor/auditoria",
  },
  {
    elemento: "Registro de medidores digitales",
    estado: "parcial",
    detalle: "Inventario por localidad disponible; falta parque migrado consolidado.",
    to: "/supervisor/catastro",
  },
];

export function RegulatoryView(): JSX.Element {
  const { toast } = useToast();
  const dqQuery = useQuery<DqMetrics>({
    queryKey: ["quality.metrics"],
    queryFn: () => api<DqMetrics>("/quality/metrics"),
    staleTime: 60_000,
  });
  const m = dqQuery.data;
  const tasaLectura = m?.cobertura_lecturas_pct ?? 0;
  const liveRf = m?._source === "rf-api";

  return (
    <>
      <PageHeader
        eyebrow="Cumplimiento"
        title="Cumplimiento SISS"
        subtitle="Indicadores y reportes alineados al Instructivo SISS de medición digital con telelectura (junio 2024)."
        actions={
          <button
            className="btn btn--primary"
            onClick={() => toast("Reporte regulatorio generado (simulación)", "success")}
          >
            <Icon name="report" size={13} /> Generar reporte SISS
          </button>
        }
      />

      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className={`kpi kpi--${tasaLectura >= 95 ? "accent" : tasaLectura >= 90 ? "warn" : "danger"}`}>
          <span className="kpi__label">Tasa de lectura efectiva</span>
          <span className="kpi__value">{tasaLectura.toFixed(1)} %</span>
          <span className="kpi__hint">
            {liveRf ? "telemetría en vivo" : "exigencia SISS"}
          </span>
        </div>
        <div className="kpi kpi--info">
          <span className="kpi__label">Gaps de lectura</span>
          <span className="kpi__value">{(m?.gaps ?? 0).toLocaleString("es-CL")}</span>
          <span className="kpi__hint">huecos a justificar</span>
        </div>
        <div className="kpi kpi--warn">
          <span className="kpi__label">Lecturas no fiables</span>
          <span className="kpi__value">{m?.lecturas_imposibles ?? 0}</span>
          <span className="kpi__hint">requieren validación</span>
        </div>
        <div className="kpi kpi--magenta">
          <span className="kpi__label">Requisitos cumplidos</span>
          <span className="kpi__value">
            {REQUISITOS_SISS.filter((r) => r.estado === "cumple").length}/{REQUISITOS_SISS.length}
          </span>
          <span className="kpi__hint">checklist Instructivo</span>
        </div>
      </div>

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card__title">
          <span>Checklist Instructivo SISS</span>
          <span className="text-faint" style={{ fontSize: 11 }}>jun-2024</span>
        </div>
        <div className="table-wrapper" style={{ borderColor: "transparent" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Elemento</th>
                <th>Estado</th>
                <th>Detalle</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {REQUISITOS_SISS.map((r) => (
                <tr key={r.elemento}>
                  <td><strong>{r.elemento}</strong></td>
                  <td>
                    <span className={`badge badge--${r.estado === "cumple" ? "accent" : "warn"}`}>
                      {r.estado}
                    </span>
                  </td>
                  <td className="text-muted" style={{ fontSize: 12.5 }}>{r.detalle}</td>
                  <td>
                    {r.to && (
                      <Link to={r.to} className="btn btn--ghost btn--icon" aria-label="Abrir">
                        <Icon name="chevron-right" size={12} />
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="card__title">Reportes regulatorios</div>
        <div className="vstack" style={{ gap: 8 }}>
          {[
            { nombre: "Informe de tasa de lectura por localidad", periodo: "Mensual" },
            { nombre: "Registro de avisos de fuga al cliente", periodo: "Mensual" },
            { nombre: "Auditoría de lecturas digitales validadas", periodo: "Trimestral" },
            { nombre: "Inventario de medidores digitales migrados", periodo: "Semestral" },
          ].map((rep) => (
            <div
              key={rep.nombre}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                gap: 12,
                alignItems: "center",
                padding: "10px 12px",
                border: "1px solid var(--c-border)",
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: 13 }}>{rep.nombre}</span>
              <span className="badge badge--neutral">{rep.periodo}</span>
              <button
                className="btn btn--ghost"
                onClick={() => toast(`"${rep.nombre}" exportado (simulación)`, "success")}
              >
                <Icon name="report" size={12} /> Exportar
              </button>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
